import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ResourceDWR } from "../../dto/ResourceDWR";
import { HoldingsInformation } from "../../dto/HoldingsInformation";
import { useLanguage } from "../Language";
import { useLocationConfiguration } from "../LocationConfiguration";
import React, { useEffect, useMemo, useState } from "react";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { useItemAvailability } from "../pages/resource/ItemAvailability";
import { useIsMobile } from "../../hooks/useIsMobile";
import { InterfaceType, PlaceHoldPayloadItem } from "../services/Holds";
import { useBranchLimits } from "../BranchLimits";
import {
  CopyPrimaryDisplay,
  CopyStatusDisplay,
  serialSort,
} from "../pages/resource/Holdings";
import { useSingleBranch } from "./useSingleBranch";
import { useOnShelfError } from "./useOnShelfError";
import { SelectedLocationsCopiesToggle } from "../SelectedLocationsCopiesToggle";
interface PlaceHoldCopiesProps {
  pickupLocationId: string | null;
  resource: ResourceDWR;
  control: Control<PlaceHoldFormInput>; // TODO use FormProvider and useFormContext instead
}
export const PlaceHoldCopies = ({
  resource,
  control,
  pickupLocationId,
}: PlaceHoldCopiesProps) => {
  const { itemAvailability, ready: itemAvailabilityReady } =
    useItemAvailability(resource.holdingsInformations, resource);
  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();
  const { allowOnShelfHolds } =
    locationConfiguration?.pacSettings?.userActionSettings || {};

  const { displayInCopiesBranchIds, selectedBranchIds } = useBranchLimits();
  const [viewSelected, setViewSelected] = useState<boolean>(true);

  // TODO update useVisibleHoldings to accept viewSelected and includeTextualHoldings
  const visibleHoldings = useMemo(() => {
    return resource.holdingsInformations.filter(({ branchIdentifier }) =>
      viewSelected && selectedBranchIds?.length
        ? selectedBranchIds.includes(branchIdentifier)
        : displayInCopiesBranchIds.includes(branchIdentifier)
    );
  }, [viewSelected, displayInCopiesBranchIds, selectedBranchIds]);

  const holdings = useMemo(() => {
    if (allowOnShelfHolds) {
      return visibleHoldings;
    }

    if (!itemAvailabilityReady) {
      return null;
    }

    return visibleHoldings.filter(
      (hi) =>
        hi.branchIdentifier !== pickupLocationId ||
        !itemAvailability.get(hi.barcode)?.available
    );
  }, [
    visibleHoldings,
    itemAvailability,
    itemAvailabilityReady,
    pickupLocationId,
  ]);

  const locationHoldingsMap = useMemo(() => {
    const groupedHoldings = new Map<string, HoldingsInformation[]>();
    if (!holdings?.length) {
      return groupedHoldings;
    }
    for (const h of holdings) {
      if (!groupedHoldings.has(h.branchName)) {
        groupedHoldings.set(h.branchName, [h]);
      } else {
        groupedHoldings.get(h.branchName).push(h);
      }
    }

    if (resource.serial) {
      for (const arr of groupedHoldings.values()) {
        arr.sort(serialSort);
      }
    }
    return groupedHoldings;
  }, [holdings, resource.serial]);

  const displayHoldingsFilteredAlert = useMemo(() => {
    return !!holdings && visibleHoldings.length !== holdings.length;
  }, [visibleHoldings, holdings]);

  return (
    <>
      <Controller<PlaceHoldFormInput, "barcodes">
        control={control}
        name={"barcodes"}
        rules={{
          required: translate("isRequired", {
            LABEL: translate("copies"),
          }),
        }}
        render={({
          field: { onChange, value },
          fieldState: { error },
          formState,
        }) => {
          return (
            <FormControl error={!!error}>
              <FormLabel>{translate("copies")}</FormLabel>
              <SelectedLocationsCopiesToggle
                resource={resource}
                viewSelected={viewSelected}
                onChange={(viewSelected) => setViewSelected(viewSelected)}
              ></SelectedLocationsCopiesToggle>
              {displayHoldingsFilteredAlert && (
                <Alert variant={"outlined"} severity={"info"}>
                  {translate("OnShelfInfoChooseFromTheseItems")}
                </Alert>
              )}
              <List
                sx={{
                  padding: 0,
                }}
              >
                {!itemAvailabilityReady ? (
                  <LinearProgress />
                ) : (
                  Array.from(locationHoldingsMap.keys())
                    .sort((a, b) => a.localeCompare(b))
                    .map((key) => {
                      const locationHoldings = locationHoldingsMap.get(key);
                      const disabled =
                        !allowOnShelfHolds && pickupLocationId === key;
                      return (
                        <React.Fragment key={key}>
                          <ListSubheader>{key}</ListSubheader>
                          {locationHoldings.map((hi) => (
                            <ListItem
                              key={hi.barcode}
                              disablePadding
                              disableGutters
                            >
                              <ListItemButton
                                dense
                                disableGutters
                                disabled={disabled}
                                onClick={() => {
                                  onChange(
                                    value.includes(hi.barcode)
                                      ? value.filter((v) => v !== hi.barcode)
                                      : [...value, hi.barcode]
                                  );
                                }}
                              >
                                <ListItemIcon>
                                  <Checkbox
                                    edge="end"
                                    disabled={disabled}
                                    checked={value.includes(hi.barcode)}
                                    tabIndex={-1}
                                    disableRipple
                                    //   inputProps={{ 'aria-labelledby': labelId }}
                                  />
                                </ListItemIcon>

                                <ListItemText
                                  primary={<CopyPrimaryDisplay holding={hi} />}
                                  secondary={
                                    <CopyStatusDisplay
                                      availability={
                                        itemAvailabilityReady &&
                                        itemAvailability.get(hi.barcode)
                                      }
                                    />
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </React.Fragment>
                      );
                    })
                )}
              </List>
              <FormHelperText>{error?.message || " "}</FormHelperText>
            </FormControl>
          );
        }}
      />
    </>
  );
};

interface PlaceHoldFormInput {
  requestType: "title" | "item";
  pickupLocation: string;
  barcodes: string[];
}

interface PlaceHoldDialogProps {
  resource: ResourceDWR;
  pickupLocationId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: PlaceHoldPayloadItem[]) => void;
}
export const PlaceHoldDialog = ({
  open,
  resource,
  pickupLocationId,
  onClose,
  onSubmit: submitHandler,
}: PlaceHoldDialogProps) => {
  const { translate } = useLanguage();
  const { branchLimits } = useBranchLimits();
  const isMobile = useIsMobile();
  const { locationConfiguration } = useLocationConfiguration();
  const { userActionSettings } = locationConfiguration?.pacSettings || {};
  const { allowItemLevelHolds, allowTitleLevelHolds } =
    userActionSettings || {};
  const { singlePickupBranchId } = useSingleBranch();

  const defaultRequestType =
    (!allowTitleLevelHolds || resource?.serial) && allowItemLevelHolds
      ? "item"
      : "title";

  const formMethods = useForm<PlaceHoldFormInput>({
    mode: "onBlur",
    defaultValues: {
      requestType: defaultRequestType,
      pickupLocation: pickupLocationId,
      barcodes: [],
    },
  });
  const { handleSubmit, control, setValue, reset } = formMethods;
  const type = useWatch({
    name: "requestType",
    control: control,
  });
  const pickupLocation = useWatch({
    name: "pickupLocation",
    control: control,
  });
  const onShelfError = useOnShelfError(resource, pickupLocation);

  useEffect(() => {
    setValue("barcodes", []);
  }, [pickupLocation]);

  useEffect(() => {
    reset();
    if (pickupLocationId) {
      setValue("pickupLocation", pickupLocationId);
    }
    setValue("requestType", defaultRequestType);
  }, [open]);

  const onSubmit = async ({
    requestType,
    pickupLocation,
    barcodes,
  }: PlaceHoldFormInput) => {
    const payload: PlaceHoldPayloadItem[] =
      requestType === "title"
        ? [
            {
              pickupBranchId: pickupLocation,
              bibliographicId: resource.hostBibliographicId,
              titleLevelHold: true,
              serialItem: resource.serial,
              downloadable: resource.downloadable,
              interfaceType: InterfaceType.PAC,
            },
          ]
        : barcodes.map((barcode) => ({
            pickupBranchId: pickupLocation,
            bibliographicId: barcode,
            titleLevelHold: false,
            serialItem: resource.serial, // FIXME check item.serialItem
            downloadable: resource.downloadable,
            interfaceType: InterfaceType.PAC,
          }));

    submitHandler(payload);
  };

  const getPickupLocationSelector = () => {
    if (singlePickupBranchId) {
      return (
        <Typography>
          {translate("pickupLocation")}{" "}
          <strong>
            {branchLimits.find((bl) => bl.identifier === singlePickupBranchId)
              ?.displayName || `${singlePickupBranchId}`}
          </strong>
        </Typography>
      );
    }

    return (
      <Controller
        name={"pickupLocation"}
        control={control}
        rules={{
          required: translate("isRequired", {
            LABEL: translate("pickupLocation"),
          }),
        }}
        render={({
          field: { ref, onChange, ...field },
          fieldState: { error },
        }) => (
          <Autocomplete
            sx={{ maxWidth: { sm: "320px" } }}
            {...field}
            onChange={(_, value) => onChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={!!error}
                helperText={error ? <>{error.message}</> : " "}
                label={translate("pickupLocation")}
                inputRef={ref}
              />
            )}
            options={(branchLimits || [])
              .filter((bl) => !bl.hidden && bl.displayInPickupLocation)
              .map((bl) => bl.identifier)}
            getOptionLabel={(option) =>
              branchLimits.find((bl) => bl.identifier === option)
                ?.displayName || `${option}`
            }
          ></Autocomplete>
        )}
      />
    );
  };

  const requestTypeSelector = useMemo(() => {
    const showTitleLevel = allowTitleLevelHolds && !resource?.serial;
    if (allowItemLevelHolds && showTitleLevel) {
      return (
        <FormControl>
          <FormLabel id="requestTypeLabel">{translate("selectOne")}</FormLabel>
          <Controller<PlaceHoldFormInput>
            name="requestType"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                aria-labelledby="requestTypeLabel"
                name="requestType"
              >
                <FormControlLabel
                  value="title"
                  control={<Radio />}
                  label={translate("titleLevelOption")}
                />
                <FormControlLabel
                  value="item"
                  control={<Radio />}
                  label={translate("itemLevelOption")}
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      );
    }
    return null;
  }, [
    allowTitleLevelHolds,
    allowItemLevelHolds,
    resource?.serial,
    translate,
    control,
  ]);

  if (!resource) {
    return null;
  }

  // Note form must be passed via slot props
  // when nesting form under Dialog, the DialogContent overflow behavior is broken
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      slotProps={{
        paper: {
          component: "form",
          noValidate: true,
          autoComplete: "off",
          onSubmit: handleSubmit(onSubmit),
        },
      }}
    >
      <DialogTitle>
        {translate("placeARequestPrefix")} {translate("request")}
      </DialogTitle>
      <DialogContent
        sx={{
          width: isMobile ? "auto" : 504,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography component="h3" variant="h6">
            {resource.shortTitle}
          </Typography>
          {getPickupLocationSelector()}
          {onShelfError && (
            <Alert severity={"error"}>{translate("OnShelfError")}</Alert>
          )}
          {!onShelfError && requestTypeSelector}

          {/* RAIL-950: if you remove unmountOnExit, you must pass type as a prop to PlaceHoldCopies and conditionally render or conditionally require */}
          <Collapse unmountOnExit in={type === "item" && !onShelfError}>
            <PlaceHoldCopies
              resource={resource}
              control={control}
              pickupLocationId={pickupLocation}
            />
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate("cancel")}</Button>
        <Button type="submit" disabled={onShelfError} variant={"contained"}>
          {translate("submit")} {translate("request")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
